<template>
  <dialog-reset
    title="备件申请详情"
    :visible.sync="visible"
    width="1000px"
    top="5vh"
    height="85vh"
    :zoomShow="false"
    :close-on-click-modal="false"
    @close="$emit('update:visible', false)"
  >
    <div class="detail-container" v-loading="loading">
      <!-- 头部操作栏 -->
      <div class="header-actions">
        <el-link type="primary" icon="el-icon-arrow-left" @click="$emit('update:visible', false)">返回申请记录列表</el-link>
        <div class="action-buttons">
          <el-button size="small" icon="el-icon-printer" @click="handlePrint">打印申请单</el-button>
          <el-button size="small" icon="el-icon-download" @click="handleExportPDF">导出为PDF</el-button>
        </div>
      </div>

      <!-- 标题和状态 -->
      <div class="title-section">
        <h3>备件申请详情 <span class="application-id">{{ detail.applyNo || '-' }}</span></h3>
        <el-tag :type="getStatusTagType(detail.applyStatus)" size="small">
          {{ getStatusLabel(detail.applyStatus) }}
        </el-tag>
      </div>

      <!-- 基本信息 -->
      <div class="card">
        <div class="card-title">基本信息</div>
        <div class="card-grid">
          <div class="item">
            <div class="label">申请单号</div>
            <div class="value">{{ detail.applyNo || '-' }}</div>
          </div>
          <div class="item">
            <div class="label">申请人</div>
            <div class="value">{{ detail.applyUserName || '-' }}</div>
          </div>
          <div class="item">
            <div class="label">申请时间</div>
            <div class="value">{{ detail.applyTime || '-' }}</div>
          </div>
          <div class="item">
            <div class="label">申请标题</div>
            <div class="value">{{ detail.applyTitle || '-' }}</div>
          </div>
          <div class="item">
            <div class="label">紧急程度</div>
            <div class="value">{{ getUrgencyLevelLabel(detail.urgencyLevel) }}</div>
          </div>
          <div class="item">
            <div class="label">期望领用时间</div>
            <div class="value">{{ detail.expectedTime || '-' }}</div>
          </div>
        </div>
      </div>

      <!-- 关联工单信息 -->
      <div class="card">
        <div class="card-title">关联工单信息</div>
        <div class="card-grid">
          <div class="item">
            <div class="label">工单编号</div>
            <div class="value">{{ detail.workOrderNo || '-' }}</div>
          </div>
          <div class="item full-width">
            <div class="label">申请原因</div>
            <div class="value">{{ detail.applyReason || '-' }}</div>
          </div>
        </div>
      </div>

      <!-- 申请配件清单 -->
      <div class="card">
        <div class="card-title">申请配件清单</div>
        <el-table :data="partsList" border size="small" style="margin-top: 12px">
          <el-table-column prop="partName" label="配件名称" minWidth="150" />
          <el-table-column prop="partCode" label="配件编码" minWidth="120" />
          <el-table-column prop="applyCount" label="申请数量" width="100" align="center" />
          <el-table-column prop="actualIssueCount" label="实际发放数量" width="120" align="center" />
          <el-table-column prop="remark" label="备注" minWidth="150" />
        </el-table>
        <div class="parts-summary">
          <span>配件总数: <strong>{{ totalPartsCount }}</strong>件</span>
          <span>总数量: <strong>{{ detail.totalCount || 0 }}</strong></span>
          <span>总金额: <strong>¥{{ detail.totalAmount || 0 }}</strong></span>
        </div>
      </div>

      <!-- 申请说明 -->
      <div class="card">
        <div class="card-title">申请说明</div>
        <div class="card-grid">
          <div class="item full-width">
            <div class="label">备注信息</div>
            <div class="value">{{ detail.remark || '-' }}</div>
          </div>
        </div>
      </div>

      <!-- 审核流程信息 -->
      <div class="card" v-if="detail.applyStatus !== 1">
        <div class="card-title">审核流程信息</div>
        <div class="card-grid">
          <div class="item full-width">
            <div class="label">审核意见</div>
            <div class="value">{{ detail.remark || '-' }}</div>
          </div>
        </div>
      </div>

      <!-- 审核操作 -->
      <div class="approval-actions" v-if="detail.applyStatus === 1">
        <el-button type="danger" icon="el-icon-close" @click="handleReject">拒绝申请</el-button>
        <el-button type="primary" icon="el-icon-check" @click="handleApprove">同意申请</el-button>
      </div>
    </div>
  </dialog-reset>
</template>

<script>
import { approve, reject, reReview, printApplication, exportPDF } from "../api";
import { APPROVAL_STATUS_DICT, APPLICATION_METHOD_DICT, MAINTENANCE_TYPE_DICT, URGENCY_LEVEL_DICT } from "../const";

export default {
  name: "ApplicationDetailDialog",
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    detail: {
      type: Object,
      default: () => ({}),
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    partsList() {
      if (!this.detail.list) return [];
      if (Array.isArray(this.detail.list)) {
        return this.detail.list;
      }
      if (typeof this.detail.list === "string") {
        try {
          return JSON.parse(this.detail.list);
        } catch (e) {
          return [];
        }
      }
      return [];
    },
    totalPartsCount() {
      return this.partsList.length;
    },
  },
  methods: {
    getStatusTagType(status) {
      const map = {
        1: "warning",  // 待审核
        2: "success",  // 已通过
        3: "danger",   // 已拒绝
      };
      return map[status] || "";
    },
    getStatusLabel(status) {
      const item = APPROVAL_STATUS_DICT.find(d => d.value === status);
      return item ? item.label : status || "-";
    },
    getApplicationMethodLabel(method) {
      const item = APPLICATION_METHOD_DICT.find(d => d.value === method);
      return item ? item.label : method || "-";
    },
    getMaintenanceTypeLabel(type) {
      const item = MAINTENANCE_TYPE_DICT.find(d => d.value === type);
      return item ? item.label : type || "-";
    },
    getUrgencyLevelLabel(level) {
      const item = URGENCY_LEVEL_DICT.find(d => d.value === level);
      return item ? item.label : level || "-";
    },
    async handleApprove() {
      try {
        await this.$confirm("确认通过该申请？", "提示", {
          type: "warning",
        });
        await approve(this.detail.id, { remark: "审核通过" });
        this.$message.success("操作成功");
        this.$emit("approve");
      } catch (e) {
        if (e !== "cancel") {
          this.$message.error("操作失败");
        }
      }
    },
    async handleReject() {
      try {
        const { value } = await this.$prompt("请输入拒绝原因", "拒绝申请", {
          confirmButtonText: "确定",
          cancelButtonText: "取消",
          inputType: "textarea",
        });
        await reject(this.detail.id, { remark: value || "拒绝申请" });
        this.$message.success("操作成功");
        this.$emit("reject");
      } catch (e) {
        if (e !== "cancel") {
          this.$message.error("操作失败");
        }
      }
    },
    async handleReReview() {
      try {
        await this.$confirm("确认重新审核该申请？", "提示", {
          type: "warning",
        });
        await reReview(this.detail.id);
        this.$message.success("操作成功");
        this.$emit("re-review");
      } catch (e) {
        if (e !== "cancel") {
          this.$message.error("操作失败");
        }
      }
    },
    async handlePrint() {
      if (!this.detail.id) {
        this.$message.warning("未找到申请单ID");
        return;
      }
      try {
        await printApplication(this.detail.id);
        this.$message.success("打印功能已调用");
      } catch (e) {
        this.$message.error("打印失败");
      }
    },
    async handleExportPDF() {
      if (!this.detail.id) {
        this.$message.warning("未找到申请单ID");
        return;
      }
      try {
        const blob = await exportPDF(this.detail.id);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${this.detail.applyNo || "申请单"}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        this.$message.success("导出成功");
      } catch (e) {
        this.$message.error("导出失败");
      }
    },
  },
};
</script>

<style scoped lang="scss">
.detail-container {
  padding: 0;
}

.header-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #ebeef5;
}

.action-buttons {
  display: flex;
  gap: 8px;
}

.title-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 1px solid #ebeef5;

  h3 {
    margin: 0;
    font-size: 18px;
    font-weight: 500;
  }

  .application-id {
    font-size: 14px;
    font-weight: normal;
    color: #909399;
    margin-left: 8px;
  }
}

.card {
  background: #fff;
  border-radius: 4px;
  padding: 16px;
  margin-bottom: 16px;
  border: 1px solid #ebeef5;
}

.card-title {
  font-size: 16px;
  font-weight: 500;
  color: #303133;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f0f0f0;
}

.card-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

.item {
  display: flex;
  flex-direction: column;
  gap: 4px;

  &.full-width {
    grid-column: 1 / -1;
  }
}

.label {
  font-size: 13px;
  color: #909399;
}

.value {
  font-size: 14px;
  color: #303133;
  word-break: break-word;
}

.parts-summary {
  display: flex;
  justify-content: space-between;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
  font-size: 14px;
  color: #606266;

  strong {
    color: #303133;
    margin-left: 4px;
  }

  .text-success {
    color: #67c23a;
  }

  .text-warning {
    color: #e6a23c;
  }
}

.approval-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #ebeef5;
}
</style>



